import { Fragment, useState } from 'react';

import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { WorkWeek } from './types';
import { WorkAgreementDetails } from 'components/types';

import { PRIMARY } from 'constants/colors';

import { getReferenceBenefit } from '../../utils';
import { getPeriodsDurationInMinutes } from 'components/employee-portal/work-pattern/utils';
import {
  formatMinutesInHours,
  fromISOString,
  getHoursMinutes,
  TimePeriod,
  WeekDay
} from 'utils/date';

import {
  createWorkPattern,
  deleteWorkPattern,
  updateWorkPattern,
  WorkPatternDuty,
  WorkPatternRequest
} from 'RestAPIs/work-patterns';

import {
  hasChangesState,
  isEditModeState,
  isEmployeePortalState,
  workPatternsState
} from './atoms';

import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import {
  ConfirmationDialog,
  DatePicker,
  OptionalEndDate,
  showSnackbar
} from 'components/shared';
import {
  GridCompact12,
  GridCompact6,
  GridContainer
} from 'components/shared/Grid/Grid.components';

const Styles = styled.div`
  .end-date {
    position: relative;

    .remove-btn {
      position: absolute;
      left: 100%;
      margin-left: 10px;
      bottom: 0;
    }
  }

  .summary {
    margin-top: 20px;
    margin-bottom: 20px;

    h3 {
      margin-bottom: 10px;
    }

    .content-box {
      min-width: 400px;
      box-sizing: border-box;
      padding: 20px;
      border: 1px solid ${PRIMARY};
      display: inline-block;
    }
  }
`;

const getWorkAgreementMinutes = (week: WorkWeek, waId?: number) => {
  let minutes = 0;
  const { nr: _, ...workWeek } = week;

  Object.values(workWeek).forEach((duties) => {
    duties
      .filter((duty) => (waId ? duty.workAgreement?.waId === waId : true))
      .forEach((duty) => {
        minutes += getPeriodsDurationInMinutes(duty.periods);
      });
  });
  return minutes;
};
const getTotalPlannedMinutes = (weeks: WorkWeek[]) =>
  weeks.reduce((acc, week) => getWorkAgreementMinutes(week), 0);
const getWorkagreementFormattedHours = (week: WorkWeek, waId: number) =>
  formatMinutesInHours(getWorkAgreementMinutes(week, waId), true);
const getFormattedHours = (hours: number) =>
  formatMinutesInHours(hours * 60, true);
const getWorkAgreementDifference = (
  week: WorkWeek,
  wa: WorkAgreementDetails,
  startDate: Date | null,
  endDate: Date | null
) =>
  formatMinutesInHours(
    Math.abs(
      (getReferenceBenefit(wa, startDate, endDate)?.weekHoursMin || 0) * 60 -
        getWorkAgreementMinutes(week, wa.waId)
    ),
    true
  );

const getContractHours = (
  workAgreement: WorkAgreementDetails,
  startDate: Date | null,
  endDate: Date | null
) => {
  const benefit = getReferenceBenefit(workAgreement, startDate, endDate);
  return benefit ? getFormattedHours(benefit?.weekHoursMin || 0) : '';
};

type Props = {
  workPatternId: number | null;
  weeks: WorkWeek[];
  startDate: string;
  endDate?: string | null;
  workAgreements: WorkAgreementDetails[];
  employeeId?: number;
  canEdit: boolean;
  onRefreshWorkPatterns: () => void;
  onCancelEdit: () => void;
  onChangeStartDate: (date: Date) => void;
};

export default function WorkPatternDetails(props: Props) {
  const { t } = useTranslation('common');
  const {
    weeks,
    startDate: initialStartDate,
    endDate: initialEndDate,
    workAgreements,
    employeeId,
    canEdit,
    onRefreshWorkPatterns,
    onCancelEdit: handleCancelEdit,
    onChangeStartDate
  } = props;
  const [startDate, setStartDate] = useState<Date | null>(
    fromISOString(initialStartDate)
  );
  const [endDate, setEndDate] = useState<Date | null>(
    initialEndDate ? fromISOString(initialEndDate) : null
  );
  const totalPlannedMinutes = getTotalPlannedMinutes(weeks);
  const [isEditMode, setInEditMode] = useRecoilState(isEditModeState);
  const [hasChanges, setHasChanges] = useRecoilState(hasChangesState);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const isEmployeePortal = useRecoilValue(isEmployeePortalState);
  const wps = useRecoilValue(workPatternsState);

  function handleSave() {
    setHasChanges(false);
    setInEditMode(false);
    showSnackbar({
      message: t('changesSaved'),
      severity: 'success'
    });
    onRefreshWorkPatterns();
  }

  const onSave = () => {
    setHasChanges(false);
    const duties: WorkPatternDuty[] = [];

    weeks.forEach((week) => {
      const { nr, ...workWeek } = week;
      Object.entries(workWeek).forEach(([day, workDay]) => {
        workDay.forEach((duty) => {
          duties.push({
            weekNumber: nr,
            dayOfWeek: day.toUpperCase() as WeekDay,
            workAgreementId: duty.workAgreement.waId,
            costCenterId: duty.planUnit?.id,
            periods: duty.periods.map(
              ({ dutyHourTypeId, startTime, endTime }) => ({
                dutyHourTypeId: dutyHourTypeId || -1,
                startTime: getHoursMinutes(startTime) as TimePeriod,
                endTime: getHoursMinutes(endTime) as TimePeriod
              })
            )
          });
        });
      });
    });
    if (!startDate) {
      return;
    }
    const request: WorkPatternRequest = {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: endDate ? format(endDate, 'yyyy-MM-dd') : null,
      numberOfWeeks: weeks.length,
      duties
    };
    if (props.workPatternId) {
      updateWorkPattern({
        request,
        workPatternId: props.workPatternId,
        employeeId,
        isEmployeePortal
      }).then(
        () => {
          handleSave();
        },
        () => {
          showSnackbar({
            message: t('changesFailed'),
            severity: 'error'
          });
        }
      );
    } else {
      createWorkPattern({ request, employeeId, isEmployeePortal }).then(
        () => {
          handleSave();
        },
        () => {
          showSnackbar({
            message: t('changesFailed'),
            severity: 'error'
          });
        }
      );
    }
  };

  const onDelete = () => {
    if (props.workPatternId) {
      setShowConfirmationDialog(true);
    }
  };

  const onDeleteConfirmation = (isConfirmed) => {
    setShowConfirmationDialog(false);
    if (isConfirmed && props.workPatternId) {
      deleteWorkPattern({
        workPatternId: props.workPatternId,
        employeeId,
        isEmployeePortal
      }).then(
        () => {
          showSnackbar({
            message: t('workPatternDeleted'),
            severity: 'success'
          });
          onRefreshWorkPatterns();
        },
        () => {
          showSnackbar({
            message: t('changesFailed'),
            severity: 'error'
          });
        }
      );
    }
  };

  const onCancelEdit = () => {
    setStartDate(fromISOString(initialStartDate));
    setEndDate(initialEndDate ? fromISOString(initialEndDate) : null);
    setHasChanges(false);
    setInEditMode(false);
    onChangeStartDate && onChangeStartDate(fromISOString(initialStartDate));
    handleCancelEdit();
  };

  return (
    <Styles>
      <div style={{ maxWidth: 400 }}>
        <GridContainer>
          <GridCompact6>
            <DatePicker
              date={startDate}
              onChange={(date) => {
                setStartDate(date);
                setHasChanges(true);
                onChangeStartDate && date && onChangeStartDate(date);
              }}
              label={t('startDate')}
              readOnly={!isEditMode}
            />
          </GridCompact6>
          <GridCompact6 className="d-flex v-bottom">
            <OptionalEndDate
              date={endDate}
              onDateChanged={(date) => {
                setEndDate(date);
                setHasChanges(true);
              }}
              readonly={!isEditMode}
            />
          </GridCompact6>
        </GridContainer>
      </div>
      <GridContainer>
        <GridCompact12 className="summary">
          <h3>{t('overview')}</h3>
          <Paper className="content-box">
            <Table size="small">
              <TableHead sx={{ '& th': { border: 0 } }}>
                <TableRow>
                  <TableCell />
                  <TableCell />
                  {weeks.map((week) => (
                    <TableCell key={week.nr} className="text-right">
                      <span className="semibold">
                        {t('week')} {week.nr}
                      </span>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell />
                  <TableCell />
                  {weeks.map((week) => (
                    <TableCell key={week.nr} />
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {workAgreements
                  .filter(
                    ({ employmentStart, employmentEnd, waId }) =>
                      ((startDate === null ||
                        startDate < new Date(employmentEnd)) &&
                        (endDate === null ||
                          endDate > new Date(employmentStart))) ||
                      weeks.some(
                        (workWeek) =>
                          getWorkAgreementMinutes(workWeek, waId) > 0
                      )
                  )
                  .map((wa, i) => (
                    <Fragment key={wa.waId}>
                      <TableRow>
                        <TableCell>
                          <span className="semibold">
                            {t('workAgreement')} {wa.nr}
                          </span>
                        </TableCell>
                        <TableCell />
                        {weeks.map((week) => (
                          <TableCell key={week.nr} />
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell />
                        <TableCell>{t('contract')}</TableCell>
                        {weeks.map((week) => (
                          <TableCell key={week.nr} className="text-right">
                            {getContractHours(wa, startDate, endDate)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell />
                        <TableCell>{t('planned')}</TableCell>
                        {weeks.map((week) => (
                          <TableCell key={week.nr} className="text-right">
                            {getWorkagreementFormattedHours(week, wa.waId)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow sx={{ '& td': { border: 0 } }}>
                        <TableCell />
                        <TableCell />
                        {weeks.map((week) => (
                          <TableCell key={week.nr} />
                        ))}
                      </TableRow>
                      <TableRow sx={{ '& td': { border: 0 } }}>
                        <TableCell />
                        <TableCell>
                          <span className="semibold">{t('difference')}</span>
                        </TableCell>
                        {weeks.map((week) => (
                          <TableCell key={week.nr} className="text-right">
                            <span className="semibold">
                              {getWorkAgreementDifference(
                                week,
                                wa,
                                startDate,
                                endDate
                              )}
                            </span>
                          </TableCell>
                        ))}
                      </TableRow>
                      {workAgreements.length > 1 &&
                        i < workAgreements.length - 1 && (
                          <TableRow sx={{ '& td': { border: 0 } }}>
                            <TableCell />
                            <TableCell />
                            {weeks.map((week) => (
                              <TableCell key={week.nr} />
                            ))}
                          </TableRow>
                        )}
                    </Fragment>
                  ))}
              </TableBody>
            </Table>
          </Paper>
        </GridCompact12>
        <GridCompact12>
          <div className="d-flex between" style={{ maxWidth: 400 }}>
            {canEdit && isEditMode && (
              <>
                <Button
                  variant="contained"
                  disabled={!(totalPlannedMinutes && hasChanges) || !startDate}
                  onClick={onSave}
                >
                  {t('save')}
                </Button>
                {(wps.length > 1 || props.workPatternId) && (
                  <Button variant="outlined" onClick={onCancelEdit}>
                    {t('cancel')}
                  </Button>
                )}
              </>
            )}
            {canEdit && !isEditMode && (
              <>
                <Button variant="outlined" onClick={() => setInEditMode(true)}>
                  {t('edit')}
                </Button>
                {props.workPatternId && (
                  <Button variant="outlined" color="error" onClick={onDelete}>
                    {t('delete')}
                  </Button>
                )}
              </>
            )}
          </div>
        </GridCompact12>
      </GridContainer>
      {showConfirmationDialog && (
        <ConfirmationDialog
          handleAction={onDeleteConfirmation}
          cancelBtnText={t('no')}
          confirmBtnText={t('yes')}
          title={t('confirmationDialog.deleteWorkPattern.title')}
          type="warning"
        >
          <p>{t('confirmationDialog.deleteWorkPattern.body')}</p>
        </ConfirmationDialog>
      )}
    </Styles>
  );
}
